"use client";

import Image from "next/image";
import Link from "@/components/ui/Link";
import { useRouter } from "next/navigation";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getSectionUrl, getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string;
  customClass?: string;
};

type Props = {
  category?: FieldConfig;
  sectionUrl?: string;
  title?: FieldConfig;
  excerpt?: FieldConfig;
  timeAgo?: FieldConfig;
  imageSrc?: FieldConfig;
  slug?: string;
  priority?: boolean;
  imageMetadata?: ImageMetadata;
  templatetype?: FieldConfig;
};

export const VerticalMediamImageCategoryNews = ({
  category = { hidden: false, value: "SPORT", customClass: "" },
  sectionUrl,
  title = { hidden: false, value: "சதம் விளாசிய க்ளாசென்: பெங்களூருக்கு 187 ரன்கள் இலக்கு! க்ளாசென்", customClass: "" },
  excerpt = { hidden: false, value: "ராயல் சேலஞ்சர்ஸ் பெங்களூருவுக்கு எதிராக முதலில் பேட் செய்த சன் ரைசர்ஸ்", customClass: "" },
  timeAgo = { hidden: false, value: "May 17, 2023", customClass: "" },
  imageSrc = { hidden: false, value: FALLBACK_IMAGE, customClass: "" },
  slug,
  priority = false,
  imageMetadata,
  templatetype  = { hidden: false, value: "", customClass: "" },
}: Props) => {
  const router = useRouter();

  const categoryValue = category?.value || "";
  const titleValue = title?.value || "";
  const excerptValue = excerpt?.value || "";
  const timeAgoValue = getTimeAgo(timeAgo?.value);
  const imageSrcValue = getImageUrl(imageSrc?.value) || FALLBACK_IMAGE;
  const isLiveBlog = templatetype?.value === "live-blog" || templatetype?.value === "true";
  const sectionUrlValue = getSectionUrl(sectionUrl);

  const handleCategoryClick = (e: React.MouseEvent) => {
    if (sectionUrlValue) {
      e.preventDefault();
      e.stopPropagation();
      router.push(sectionUrlValue);
    }
  };

  const content = (
    <div className="flex flex-col bg-F8F8F8 overflow-hidden p-4 gap-4">
      {/* IMAGE */}
      {!imageSrc?.hidden && (
        <div>
          <div className="relative w-full aspect-[16/9] overflow-hidden">
            <Image
              src={imageSrcValue}
              alt={titleValue}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 300px"
              className={`object-cover w-full h-full ${imageSrc?.customClass || ""}`}
              style={getFocusPointStyle(imageMetadata)}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              priority={priority}
              placeholder="blur"
              blurDataURL={BLUR_PLACEHOLDER}
            />
          </div>
        </div>
      )}

      {/* CONTENT */}
      <div className="flex flex-col gap-3">
        {/* Category */}
        {!category?.hidden && (
          sectionUrlValue ? (
            <span
              className={`text-11-tamil-600 text-012061 tracking-wide uppercase hover:underline cursor-pointer ${category?.customClass || ""}`}
              onClick={handleCategoryClick}
            >
              {categoryValue}
            </span>
          ) : (
            <div className={`text-11-tamil-600 text-012061 tracking-wide uppercase ${category?.customClass || ""}`}>
              {categoryValue}
            </div>
          )
        )}

        {/* Title */}
        {!title?.hidden && (
          <h3 className={`text-14-tamil-700 leading-[1.4] text-1B1B1B line-clamp-2 ${title?.customClass || ""}`}>
            {isLiveBlog && (
              <span className="inline-flex items-center mr-2 align-middle">
                <span className="relative inline-flex h-4 w-4 items-center justify-center">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-40 animate-[wave_1.5s_ease-out_infinite]" />
                  <span className="absolute inline-flex h-3 w-3 rounded-full bg-red-500 opacity-60 animate-[wave_1.5s_ease-out_0.3s_infinite]" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                </span>
              </span>
            )}
            {titleValue}
          </h3>
        )}

        {/* Excerpt */}
        {!excerpt?.hidden && (
          <p className={`text-12-tamil-500 text-4D4D4D leading-relaxed line-clamp-2 ${excerpt?.customClass || ""}`}>
            {excerptValue}
          </p>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center">
          {!timeAgo?.hidden && (
            <span className={`text-12-tamil-500 text-1B1B1B ${timeAgo?.customClass || ""}`}>{timeAgoValue}</span>
          )}
          <div className="flex gap-3">
            <MessageIcon
              id="message-icon-1"
              slug={slug}
              category={BOOKMARK_CATEGORIES.NEWS}
              title={titleValue}
              thumbnailUrl={imageSrcValue}
              description={excerptValue}
              articleUrl={slug ? `/${slug}` : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (slug) {
    return (
      <Link href={`/${slug}`} className="block hover:bg-gray-100 transition-colors cursor-pointer">
        {content}
      </Link>
    );
  }

  return content;
};
